import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Shield } from 'lucide-react';
import LanguageSelector from '@/components/LanguageSelector';
import ThemeToggle from '@/components/ThemeToggle';
import EmailAuthForm from '@/components/EmailAuthForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showEmailForm, setShowEmailForm] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        await axios.get(`${API}/auth/me`, { withCredentials: true });
        navigate('/');
      } catch (error) {
        // Not authenticated
      }
    };
    checkAuth();
  }, [navigate]);

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailAuthSuccess = (data) => {
    // Navigate to home with user data in state so ProtectedRoute doesn't need to re-check
    navigate('/', { state: { user: data.user } });
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative">
      {/* Language selector and theme toggle in top right */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <LanguageSelector />
        <ThemeToggle />
      </div>

      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-zinc-800 border-2 border-[#18181B] dark:border-zinc-600 rounded-2xl hard-shadow p-8 text-center">
          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FF8BA7] rounded-full border-2 border-[#18181B] dark:border-zinc-600 mb-4">
              <svg className="w-8 h-8 text-[#18181B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h1 className="text-4xl font-bold mb-2 text-zinc-900 dark:text-white" data-testid="login-title">{t('app.name')}</h1>
            <p className="text-zinc-500 dark:text-zinc-400">{t('app.tagline')}</p>
          </div>

          {showEmailForm ? (
            <EmailAuthForm 
              onSuccess={handleEmailAuthSuccess}
              onSwitchToGoogle={handleGoogleLogin}
            />
          ) : (
            <>
              {/* Google Login Button */}
              <button
                onClick={handleGoogleLogin}
                data-testid="google-login-btn"
                className="w-full rounded-full border-2 border-[#18181B] bg-[#18181B] text-white font-bold py-3 px-6 hover:bg-[#333] hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] active:translate-y-1 active:shadow-none transition-all flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                {t('auth.loginWithGoogle')}
              </button>

              {/* Divider */}
              <div className="flex items-center gap-4 my-6">
                <div className="flex-1 h-px bg-zinc-200 dark:bg-zinc-600"></div>
                <span className="text-xs text-zinc-400 dark:text-zinc-500">{t('auth.orContinueWith', 'o')}</span>
                <div className="flex-1 h-px bg-zinc-200 dark:bg-zinc-600"></div>
              </div>

              {/* Email/Password Button */}
              <button
                onClick={() => setShowEmailForm(true)}
                data-testid="email-login-btn"
                className="w-full rounded-full border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white font-bold py-3 px-6 hover:bg-zinc-50 dark:hover:bg-zinc-600 hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] active:translate-y-1 active:shadow-none transition-all flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {t('auth.loginWithEmail', 'Email y contraseña')}
              </button>
            </>
          )}

          <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-6">
            {t('app.description')}
          </p>

          {/* Privacy Policy Link */}
          <Link 
            to="/privacy-policy" 
            className="inline-flex items-center gap-1.5 text-xs text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300 mt-4 transition-colors"
            data-testid="privacy-policy-link"
          >
            <Shield className="w-3 h-3" />
            {t('legal.privacyPolicy')}
          </Link>
        </div>
      </div>
    </div>
  );
}
